import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Building2, LogOut, FileText, Shield, Mail } from 'lucide-react';
import { PushToggleCard } from './PushToggleCard';
import { ChangeEmailCard } from './ChangeEmailCard';
import { ShareAppCard } from './ShareAppCard';
import { ContactCard } from './ContactCard';
import { DeleteAccountCard } from './DeleteAccountCard';
import { useData } from '../contexts/DataContext';
import { teamApi } from '../lib/teamApi';
import { toast } from 'sonner';
import { useState } from 'react';

/**
 * Read-only Profile view shown to invited team MEMBERS.
 * The company profile (name, logo, taxes, RBQ, address) belongs to the
 * subscription OWNER and cannot be modified by members. Members keep
 * full control of their personal account (email, password, notifications,
 * language, logout, delete).
 */
export function TeamMemberView({ ownerCompany, ownerEmail, lang, onSignOut }) {
  const [leaving, setLeaving] = useState(false);

  const handleLeaveTeam = async () => {
    if (!window.confirm(
      lang === 'fr'
        ? "Es-tu certain(e) de vouloir quitter cette équipe ? Tu perdras l'accès aux soumissions et clients partagés."
        : 'Are you sure you want to leave this team? You will lose access to shared submissions and clients.'
    )) return;
    setLeaving(true);
    try {
      await teamApi.leave();
      // Clear local cache and hard-reload so the user reverts to their own account state.
      try {
        const { clearAllData } = await import('../lib/idb');
        await clearAllData();
        Object.keys(localStorage).forEach((k) => {
          if (k.startsWith('soumissionpro:effective_owner:') ||
              k.startsWith('soumissionpro:team_role:') ||
              k.startsWith('soumissionpro:owner_info:')) {
            localStorage.removeItem(k);
          }
        });
      } catch (e) {
        console.warn('cache clear failed', e);
      }
      toast.success(lang === 'fr' ? "Tu as quitté l'équipe." : 'You left the team.');
      window.location.href = '/';
    } catch (e) {
      toast.error(e.message || 'Erreur');
      setLeaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="team-member-view">
      {/* Banner: Read-only mode */}
      <div
        className="rounded-2xl p-4 border-2"
        style={{ backgroundColor: '#FAF7F2', borderColor: '#C9A961' }}
        data-testid="team-member-readonly-banner"
      >
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#C9A961' }}
          >
            <Eye className="w-5 h-5" style={{ color: '#5A1620' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#7A1F2B' }}>
              {lang === 'fr' ? 'Compte membre · Lecture seule' : 'Member account · Read-only'}
            </p>
            <p className="text-sm leading-relaxed" style={{ color: '#5A1620' }}>
              {lang === 'fr'
                ? "Tu fais partie d'une équipe. Le profil de l'entreprise (nom, logo, taxes, RBQ, adresse) est géré par le propriétaire de l'abonnement et ne peut pas être modifié ici."
                : "You are part of a team. The company profile (name, logo, taxes, RBQ, address) is managed by the subscription owner and cannot be modified here."}
            </p>
          </div>
        </div>
      </div>

      {/* Owner company card */}
      <div className="card p-4" data-testid="team-member-owner-card">
        <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-3">
          {lang === 'fr' ? 'Entreprise (propriétaire)' : 'Company (owner)'}
        </div>
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#7A1F2B' }}
          >
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-lg leading-tight truncate" style={{ color: '#5A1620' }}>
              {ownerCompany || (lang === 'fr' ? "Équipe" : 'Team')}
            </div>
            {ownerEmail && (
              <div className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                <Mail className="w-3 h-3" />
                {ownerEmail}
              </div>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3 leading-relaxed">
          {lang === 'fr'
            ? "Pour modifier les informations de l'entreprise, contacte le propriétaire de l'abonnement."
            : 'To change company information, contact the subscription owner.'}
        </p>
      </div>

      {/* Personal account section */}
      <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 pt-4 px-1">
        {lang === 'fr' ? 'Mon compte personnel' : 'My personal account'}
      </div>

      <ChangeEmailCard />
      <PushToggleCard />
      <ShareAppCard />
      <ContactCard />

      <div className="grid grid-cols-2 gap-2 pt-2">
        <Link to="/terms" data-testid="link-terms" className="btn-outline gap-2 !min-h-[44px] !text-xs">
          <FileText size={14} /> {lang === 'fr' ? 'Conditions' : 'Terms'}
        </Link>
        <Link to="/privacy" data-testid="link-privacy" className="btn-outline gap-2 !min-h-[44px] !text-xs">
          <Shield size={14} /> {lang === 'fr' ? 'Privé' : 'Privacy'}
        </Link>
      </div>

      {/* Leave team */}
      <button
        onClick={handleLeaveTeam}
        disabled={leaving}
        data-testid="leave-team-btn"
        className="btn-outline w-full mt-2 gap-2 !text-orange-700 !border-orange-300 hover:!bg-orange-50"
      >
        {leaving
          ? (lang === 'fr' ? 'En cours…' : 'Leaving…')
          : (lang === 'fr' ? "Quitter l'équipe" : 'Leave team')}
      </button>

      {/* Logout */}
      <button
        data-testid="logout-btn"
        onClick={onSignOut}
        className="btn-outline w-full mt-2 gap-2 !text-red-700 !border-red-300 hover:!bg-red-50"
      >
        <LogOut size={18} /> {lang === 'fr' ? 'Déconnexion' : 'Logout'}
      </button>

      <div className="mt-4">
        <DeleteAccountCard />
      </div>
    </div>
  );
}
